import { memo, useMemo } from 'react';
import { Link } from '@appsumo/dorado-react';

import UserTieIcon from '~/public/font-awesome/user-tie.png';
import type {
  FTFCompanyDetailItem,
  FTFCompanyDetailsProps,
  FTFWrapperProps,
} from '~/types/product';

import FTFCompanyDetails from './CompanyDetails/FTFCompanyDetails';
import FTFLinks from './Links/FTFLinks';
import FTFPosts from './Posts/FTFPosts';

export const FTF_WRAPPER_ELEMENT_ID = 'from-the-founders';

export default memo(function FTFWrapper({
  companyName,
  companyUrl,
  founders,
}: Readonly<FTFWrapperProps>) {
  const companyDetailItems = useMemo(() => {
    const founderUser = founders.posts?.[0]?.user || {};
    const founderName =
      [founderUser.first_name, founderUser.last_name].join(' ').trim() ||
      founderUser.username ||
      '';

    const founderItem: FTFCompanyDetailItem = founderName
      ? {
          icon: UserTieIcon,
          label: 'Founder',
          content: (
            <div className="flex flex-col md:flex-row md:items-center">
              <Link
                href={`/profile/${founderUser.username}/`}
                className="text-bolt"
                styled
                target="_blank"
              >
                {founderName}
              </Link>
              {founderUser.title && (
                <>
                  <div className="mx-2 hidden h-4 border-l-[1px] border-sundog md:block" />
                  <span>{founderUser.title}</span>
                </>
              )}
            </div>
          ),
        }
      : ({} as FTFCompanyDetailItem);

    return [{ ...founderItem }];
  }, [founders.posts]);

  const companyDetails = useMemo(() => {
    return {
      companyName,
      companyUrl,
      items: companyDetailItems,
    } as FTFCompanyDetailsProps;
  }, [companyDetailItems, companyName, companyUrl]);

  if (!founders.posts.length) return null;

  return (
    <section
      id={FTF_WRAPPER_ELEMENT_ID}
      className="appsumo-breakout relative mt-12 bg-iceberg"
      data-testid="ftf-wrapper"
    >
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-8 lg:py-8">
        <div className="grid grid-cols-founders grid-rows-founders gap-x-8 gap-y-6 grid-areas-founders md:grid-cols-desktop-founders md:grid-rows-desktop-founders md:grid-areas-desktop-founders">
          <FTFCompanyDetails {...companyDetails} />
          <FTFLinks links={founders.links} />
          <FTFPosts
            companyName={companyDetails.companyName}
            posts={founders.posts}
          />
        </div>
      </div>
    </section>
  );
});
